import React, { useState } from "react";
import { Plus, Trash2, CheckCircle2, AlertTriangle, Cpu, Tag, DollarSign, Star } from "lucide-react";

const ALL_CAPABILITIES = [
  { id: "text", label: "Texto" },
  { id: "reasoning", label: "Razonamiento" },
  { id: "code", label: "Código" },
  { id: "streaming", label: "Streaming" },
  { id: "vision", label: "Visión multimodal (Etapa 2C)" },
];

export const formatLLMTokenRates = (costIn, costOut) => {
  const inVal = costIn !== null && costIn !== undefined ? costIn : 0;
  const outVal = costOut !== null && costOut !== undefined ? costOut : 0;
  return `US$${inVal} / millón de tokens de entrada · US$${outVal} / millón de tokens de salida`;
};

export const LLMModelsConfig = ({ models = [], onChange, readOnly = false }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [modelForm, setModelForm] = useState({
    id: "",
    label: "",
    context_window: 64000,
    capabilities: ["text"],
    is_default: false,
    cost_per_m_input_usd: "",
    cost_per_m_output_usd: "",
    is_rate_verified: false,
  });

  const handleStartAdd = () => {
    setModelForm({
      id: "",
      label: "",
      context_window: 64000,
      capabilities: ["text"],
      is_default: models.length === 0,
      cost_per_m_input_usd: "",
      cost_per_m_output_usd: "",
      is_rate_verified: false,
    });
    setEditingIndex("new");
  };

  const handleStartEdit = (idx) => {
    const m = models[idx];
    setModelForm({
      id: m.id,
      label: m.label,
      context_window: m.context_window || 64000,
      capabilities: m.capabilities || ["text"],
      is_default: Boolean(m.is_default),
      cost_per_m_input_usd: m.cost_per_m_input_usd !== null && m.cost_per_m_input_usd !== undefined ? m.cost_per_m_input_usd : "",
      cost_per_m_output_usd: m.cost_per_m_output_usd !== null && m.cost_per_m_output_usd !== undefined ? m.cost_per_m_output_usd : "",
      is_rate_verified: Boolean(m.is_rate_verified),
    });
    setEditingIndex(idx);
  };

  const handleSaveModel = () => {
    if (!modelForm.id.trim() || !modelForm.label.trim()) return;

    const formattedModel = {
      id: modelForm.id.trim(),
      label: modelForm.label.trim(),
      context_window: Number(modelForm.context_window) || 64000,
      capabilities: modelForm.capabilities,
      is_default: Boolean(modelForm.is_default),
      cost_per_m_input_usd: modelForm.cost_per_m_input_usd !== "" ? Number(modelForm.cost_per_m_input_usd) : null,
      cost_per_m_output_usd: modelForm.cost_per_m_output_usd !== "" ? Number(modelForm.cost_per_m_output_usd) : null,
      is_rate_verified: Boolean(modelForm.is_rate_verified),
    };

    let updatedList = [...models];

    // Si este modelo se marca como default, desmarcar los demás
    if (formattedModel.is_default) {
      updatedList = updatedList.map((m) => ({ ...m, is_default: false }));
    }

    if (editingIndex === "new") {
      updatedList.push(formattedModel);
    } else {
      updatedList[editingIndex] = formattedModel;
    }

    onChange(updatedList);
    setEditingIndex(null);
  };

  const handleDeleteModel = (idx) => {
    const updatedList = models.filter((_, i) => i !== idx);
    // Si borramos el default y quedan otros, marcar el primero como default
    if (models[idx]?.is_default && updatedList.length > 0) {
      updatedList[0].is_default = true;
    }
    onChange(updatedList);
    if (editingIndex === idx) setEditingIndex(null);
  };

  const toggleCapability = (capId) => {
    const exists = modelForm.capabilities.includes(capId);
    const newCaps = exists
      ? modelForm.capabilities.filter((c) => c !== capId)
      : [...modelForm.capabilities, capId];
    setModelForm({ ...modelForm, capabilities: newCaps });
  };

  return (
    <div data-testid="llm-models-config" className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xs font-semibold text-[#ededed]">Modelos y Capacidades</h4>
          <p className="text-[11px] text-[#8a8a8a]">
            Configuración dinámica por proveedor. No se fijan modelos obsoletos.
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            data-testid="llm-add-model-btn"
            onClick={handleStartAdd}
            className="v-btn-secondary v-btn-xs inline-flex items-center gap-1.5"
          >
            <Plus size={13} />
            <span>Añadir Modelo</span>
          </button>
        )}
      </div>

      {/* Lista de Modelos */}
      <div className="flex flex-col gap-2">
        {models.length === 0 ? (
          <div
            data-testid="llm-models-empty"
            className="rounded-xl border border-dashed border-[#2a2a2a] bg-[#121212] p-4 text-center text-xs text-[#737373]"
          >
            No hay modelos configurados para este proveedor. Añada al menos uno para su uso en el Creative Director.
          </div>
        ) : (
          models.map((m, idx) => (
            <div
              key={m.id || idx}
              data-testid={`llm-model-row-${m.id}`}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-[#262626] bg-[#161616] p-3 text-xs"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#ededed]">{m.label}</span>
                  <span className="rounded bg-[#222] px-1.5 py-0.5 font-mono text-[10px] text-[#a3a3a3]">
                    {m.id}
                  </span>
                  {m.is_default && (
                    <span className="inline-flex items-center gap-1 rounded bg-[#f5b544]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#f5b544]">
                      <Star size={10} fill="#f5b544" /> Principal
                    </span>
                  )}
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[#8a8a8a]">
                  <span>Contexto: {m.context_window ? `${m.context_window.toLocaleString()} tok` : "Desconocido"}</span>
                  <span>·</span>
                  <div className="flex flex-wrap gap-1">
                    {(m.capabilities || []).map((cap) => (
                      <span
                        key={cap}
                        className="rounded bg-[#202020] px-1 py-0.5 text-[10px] text-[#c2c2c2]"
                      >
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-1 flex items-center gap-2 text-[11px]">
                  {m.is_rate_verified ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400 font-mono text-[10px]">
                      <CheckCircle2 size={11} /> Tarifa verificada ({formatLLMTokenRates(m.cost_per_m_input_usd, m.cost_per_m_output_usd)})
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[#888] font-mono text-[10px]">
                      <AlertTriangle size={11} className="text-amber-500/80" /> Tarifa desconocida / no verificada
                    </span>
                  )}
                </div>
              </div>

              {!readOnly && (
                <div className="flex items-center gap-1 self-end sm:self-center">
                  <button
                    type="button"
                    data-testid={`llm-edit-model-btn-${m.id}`}
                    onClick={() => handleStartEdit(idx)}
                    className="rounded px-2 py-1 text-xs text-[#a3a3a3] hover:bg-[#222] hover:text-[#ededed]"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    data-testid={`llm-delete-model-btn-${m.id}`}
                    onClick={() => handleDeleteModel(idx)}
                    className="rounded p-1 text-[#8a8a8a] hover:bg-red-500/10 hover:text-red-400"
                    title="Eliminar modelo"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Editor Modal / Subformulario */}
      {editingIndex !== null && (
        <div
          data-testid="llm-model-editor-form"
          className="rounded-xl border border-[#303030] bg-[#181818] p-4 text-xs flex flex-col gap-3"
        >
          <div className="font-semibold text-[#ededed]">
            {editingIndex === "new" ? "Añadir Modelo" : `Editar Modelo: ${modelForm.id}`}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[#a3a3a3]">ID Técnico (ej. deepseek-chat) *</span>
              <input
                data-testid="llm-model-id-input"
                className="v-input text-xs"
                value={modelForm.id}
                onChange={(e) => setModelForm({ ...modelForm, id: e.target.value })}
                placeholder="deepseek-chat"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[#a3a3a3]">Nombre Visible *</span>
              <input
                data-testid="llm-model-label-input"
                className="v-input text-xs"
                value={modelForm.label}
                onChange={(e) => setModelForm({ ...modelForm, label: e.target.value })}
                placeholder="DeepSeek-V3"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[#a3a3a3]">Ventana de Contexto (tokens)</span>
              <input
                type="number"
                data-testid="llm-model-context-input"
                className="v-input text-xs"
                value={modelForm.context_window}
                onChange={(e) => setModelForm({ ...modelForm, context_window: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[#a3a3a3]">Coste entrada (US$ / millón de tokens)</span>
              <input
                type="number"
                step="0.01"
                data-testid="llm-model-cost-in-input"
                className="v-input text-xs font-mono"
                placeholder="Desconocido"
                value={modelForm.cost_per_m_input_usd}
                onChange={(e) => setModelForm({ ...modelForm, cost_per_m_input_usd: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-[#a3a3a3]">Coste salida (US$ / millón de tokens)</span>
              <input
                type="number"
                step="0.01"
                data-testid="llm-model-cost-out-input"
                className="v-input text-xs font-mono"
                placeholder="Desconocido"
                value={modelForm.cost_per_m_output_usd}
                onChange={(e) => setModelForm({ ...modelForm, cost_per_m_output_usd: e.target.value })}
              />
            </label>
          </div>

          {/* Capacidades */}
          <div>
            <span className="block text-[11px] text-[#a3a3a3] mb-1.5">Capacidades Declaradas:</span>
            <div className="flex flex-wrap gap-2">
              {ALL_CAPABILITIES.map((cap) => {
                const checked = modelForm.capabilities.includes(cap.id);
                return (
                  <button
                    key={cap.id}
                    type="button"
                    data-testid={`llm-cap-${cap.id}`}
                    onClick={() => toggleCapability(cap.id)}
                    className={`rounded-lg border px-2.5 py-1 text-[11px] transition-colors ${
                      checked
                        ? "border-[#f5b544]/50 bg-[#f5b544]/10 text-[#f5b544] font-medium"
                        : "border-[#2c2c2c] bg-[#202020] text-[#888] hover:border-[#383838]"
                    }`}
                  >
                    {cap.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Flags adicionales */}
          <div className="flex flex-wrap items-center gap-4 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="llm-model-verified-checkbox"
                checked={modelForm.is_rate_verified}
                onChange={(e) => setModelForm({ ...modelForm, is_rate_verified: e.target.checked })}
                className="rounded border-[#383838] bg-[#222] text-[#f5b544]"
              />
              <span className="text-[11px] text-[#a3a3a3]">Tarifa verificada contractualmente</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="llm-model-default-checkbox"
                checked={modelForm.is_default}
                onChange={(e) => setModelForm({ ...modelForm, is_default: e.target.checked })}
                className="rounded border-[#383838] bg-[#222] text-[#f5b544]"
              />
              <span className="text-[11px] text-[#f5b544]">Establecer como modelo principal</span>
            </label>
          </div>

          {/* Botones subformulario */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#262626]">
            <button
              type="button"
              onClick={() => setEditingIndex(null)}
              className="v-btn-ghost v-btn-xs"
            >
              Cancelar
            </button>
            <button
              type="button"
              data-testid="llm-model-save-btn"
              onClick={handleSaveModel}
              disabled={!modelForm.id.trim() || !modelForm.label.trim()}
              className="v-btn-primary v-btn-xs"
            >
              Guardar Modelo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LLMModelsConfig;
